// import { useQuery } from '@tanstack/react-query'; // re-enable for Dashboard sub-site dropdown

import { Bell, Menu, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, NavLink, useLocation } from 'react-router-dom';
import IconLogout from '@/assets/icon/icon-db-logout.svg';
import IconUser from '@/assets/icon/icon-db-people.svg';
import IconCalendar from '@/assets/pedestrian/icon/icon-calendar.svg';
import IconTime from '@/assets/pedestrian/icon/icon-clock.svg';
import Logo from '@/assets/smartcity/logo.svg';
import LogoLight from '@/assets/smartcity/logo-light.svg';
import useAuth from '@/domains/auth/hooks/useAuth';
import ModalProfileManagement from '@/domains/profile-management/components/ModalProfileManagement/ModalProfileManagement';
import { useUserManagement } from '@/domains/system-management/hooks/useUserManagement';
import SwitchButton from '@/domains/traffic-monitoring/components/SwitchButton/SwitchButton';
// import { dashboardApi } from '@/domains/traffic-monitoring/apis/dashboard'; // re-enable for Dashboard sub-site dropdown
import { cn } from '@/lib/utils';
import NoticeMessage from '@/plugin/noticemessage/noticemessage';
import type { NavbarItem } from '@/shared/components/layout/Sidebar/Sidebar.types';
import { Button } from '@/shared/components/ui/Button';
import ThemeToggle from '@/shared/components/ui/ThemeToggle/ThemeToggle';
import { DASHBOARD_TABS, MANAGEMENT_SECTIONS } from '@/shared/config/headerTabs';
import { URLS } from '@/shared/config/urls';
import { usePreferenceContext } from '@/shared/context/preferenceContext';
import useCommonCodes from '@/shared/hooks/useCommonCodes';
import i18n from '@/shared/utils/i18n';
import { getFirstAccessibleRoute, getFirstManagementRoute } from '@/shared/utils/navigation';
import Common from '@/shared/utils/standard';
import { useAuthStore } from '@/store/authStore';
import { useThemeStore } from '@/store/themeStore';
import type { HeaderProps, NavigationItem } from './Header.types';

// const dataHeader = [
//   { name: 'Integrated Smart City', link: '/dashboard/smartCity-dashboard' },
//   { name: 'Traffic', link: '/dashboard/psc-dashboard' },
//   { name: 'PSC', link: '/dashboard/psc-dashboard' },
//   { name: 'CCTV', link: 'dashboard/ambulance-dashboard' },
//   { name: 'management', link: '/manage-dashboard' },
// ];

// `name` is the stable key used for access checks; `tag` is the cm_language
// tag the tab is rendered with (eng/ind/kor rows in 51.cm_language_insert.sql).
// Tab routes come from the shared model so they cannot drift from the route
// guard in shared/utils/navigation.ts.
const dataHeader = [
  ...DASHBOARD_TABS.map((tab) => ({
    name: tab.name,
    tag: tab.tag,
    link: tab.grant,
    activePaths: tab.paths,
  })),
  {
    name: 'Management',
    tag: 'cmn>nav>management',
    // Placeholder only - the rendered tab uses `managementRoute` below.
    link: '/system-management/program',
    activePaths: ['/management', ...MANAGEMENT_SECTIONS],
  },
];

const Header = ({ className }: HeaderProps) => {
  const location = useLocation();
  const { t } = useTranslation();
  const { user_id, account_type } = useAuthStore();
  const { useUser } = useUserManagement();
  const { data: userInfo } = useUser(user_id);
  const { preferenceData } = usePreferenceContext();
  const [time, setTime] = useState<string>('');
  const [date, setDate] = useState<string>('');
  const [day, setDay] = useState<string>('');
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  // Nav tabs collapse into a drawer below `lg` (1150px) - see visibleHeaderItems
  const [isNavOpen, setIsNavOpen] = useState<boolean>(false);
  const navRef = useRef<HTMLDivElement>(null);
  const { handleLogout, handleAuthority } = useAuth({ userID: user_id });

  const [optionParams] = useState<string>('upper-code=021&upper-code=ORG');
  const { commonListData } = useCommonCodes({ optionParams });

  // Authority Management (system-management/authority) grants each group a
  // set of accessible programs; handleAuthority is that set for the current
  // user (same navbarList Sidebar.tsx uses to build the management menu).
  // Management has no single page behind it, so the tab carries the first
  // section this user is actually authorized for instead of a fixed route -
  // a group whose grants cover only some sections still lands on a usable page.
  const managementRoute = getFirstManagementRoute(handleAuthority);
  // Dashboard tabs (Smart City/Traffic/PSC/CCTV) are the DASHBOARD group's own
  // children - their `link` matches the url a child Program authorizes, so
  // gate each tab on that url being present rather than showing all four
  // unconditionally (e.g. a PSC-only group should not see Smart City/Traffic).
  const authorizedDashboardUrls = new Set(
    (handleAuthority ?? []).flatMap((item) => item.urls ?? []).filter((url): url is string => !!url)
  );
  const visibleHeaderItems = dataHeader.flatMap((item) => {
    if (item.name === 'Management') {
      return managementRoute ? [{ ...item, link: managementRoute }] : [];
    }
    return authorizedDashboardUrls.has(item.link) ? [item] : [];
  });
  const logoRoute = getFirstAccessibleRoute(handleAuthority) ?? location.pathname;

  // A healthcare account only ever sees the PSC tab (the others are already
  // filtered out above by authorizedDashboardUrls), so relabeling it here is
  // enough to make clear whose PSC view this is - no cm_language tag for this
  // yet, so it's a literal override rather than a second translation key.
  const navLabel = (item: (typeof dataHeader)[number]): string =>
    item.name === 'PSC' && account_type === 'healthcare' ? 'Healthcare PSC' : t(item.tag);

  /**
   * Handle logout confirmation dialog
   */
  const handleLogoutButton = () => {
    // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
    const checkLogout = new NoticeMessage(t('msg > logout confirm'), {
      mode: 'confirm',
    });

    checkLogout.confirmClicked().then(() => {
      handleLogout();
    });
  };

  // Initialize language and set up date/time updates
  // Initialize language and set up date/time updates
  useEffect(() => {
    const updateDateTime = () => {
      const now = new Date();
      const hours = now.getHours().toString().padStart(2, '0');
      const minutes = now.getMinutes().toString().padStart(2, '0');
      const seconds = now.getSeconds().toString().padStart(2, '0');

      // eng | ind only; anything else (e.g. leftover 'kor' session) falls back to eng
      const lang = i18n.language === 'eng' ? 'eng' : 'ind';
      const langSource = Common.getDTPLangSource(lang);
      const dayName = langSource?.days[now.getDay()] || '';
      const monthName = langSource?.months[now.getMonth()] || '';
      const day = now.getDate();
      const year = now.getFullYear();

      setTime(`${hours}:${minutes}:${seconds}`);
      if (lang === 'eng') {
        setDate(`${monthName} ${day}, ${year}`); // August 11, 2026
      } else {
        setDate(`${day} ${monthName} ${year}`); // 11 Agustus 2026
      }
      setDay(dayName);
    };

    updateDateTime();
    const intervalId = setInterval(updateDateTime, 1000);

    return () => clearInterval(intervalId);
  }, []);

  const isPathActive = (activePaths: string[]) =>
    activePaths.some((path) => location.pathname.startsWith(path));

  // Get title from preferences
  const title = preferenceData?.find((item) => item.preference_key === 'title')?.preference_value;
  // Get navigation logo from preferences (uploaded via System Management)
  const navLogo = preferenceData?.find(
    (item) => item.preference_key === 'nav_logo'
  )?.preference_value;

  const isDark = useThemeStore((state) => state.isDark);

  return (
    <>
      <header
        className={cn(
          'w-full flex flex-row border-b bg-border justify-between h-[56px] lg:h-[62px] text-text-white bg-db-black',
          className
        )}
      >
        <div className="flex min-w-0 flex-1">
          <div className="flex items-center h-full shrink-0 min-w-[64px] md:min-w-[100px] bg-header-content px-[12px] md:pr-0 md:pl-[20px]">
            <Link
              to={logoRoute}
              className="m-auto flex items-center gap-[8px] h5medium text-db-text hover:opacity-75 cursor-pointer transition-opacity"
            >
              <img
                src={isDark ? LogoLight : Logo}
                alt="logo"
                className="h-[36px] md:h-[54px] w-auto object-contain"
              />
            </Link>
          </div>

          {/* naviation hamburger */}
          <Button
            variant="ghost"
            type="button"
            aria-label="Open navigation"
            className="lg:hidden h-auto px-[12px] py-0 text-db-text hover:bg-transparent hover:opacity-80"
            onClick={() => setIsNavOpen(true)}
          >
            <Menu size={22} />
          </Button>

          <div className="hidden lg:inline-flex items-center gap-1 xl:gap-3 pl-[20px] xl:pl-[50px]">
            {/* {dataHeader.map((item) => (
              <NavLink
                key={item.name}
                to={item.link}
                className={({ isActive }) =>
                  `title1 px-4 py-2 rounded-[12px] capitalize ${isActive ? 'bg-[#007AFF]/10 !text-text-blue-100' : 'bg-transparent text-text-grey'}`
                }
              >
                <span>{item.name}</span>
              </NavLink>
            ))} */}

            {visibleHeaderItems.map((item) => (
              <NavLink
                key={item.name}
                to={item.link}
                className={cn(
                  'title1 whitespace-nowrap px-2 xl:px-4 py-2 rounded-[12px] capitalize transition-colors',
                  isPathActive(item.activePaths)
                    ? 'bg-[#007AFF]/10 text-text-blue-100'
                    : 'text-text-grey'
                )}
              >
                {navLabel(item)}
              </NavLink>
            ))}
          </div>
        </div>

        {/* Right side: Date/Time and User info */}
        <div className="flex gap-[8px] w-fit shrink-0">
          {/* Below md the date/time moves into the drawer instead of squeezing the bar */}
          <div className="hidden md:flex w-fit flex-row h-full items-center gap-[24px]">
            {/* Time display */}
            <div className="flex flex-row items-center gap-[8px] xl:gap-[12px]">
              <img src={IconTime} alt="Time" className="icon-adaptive" />
              <span className="hidden xl:inline text-db-text title2 whitespace-nowrap">
                {date} <span className="text-db-text title2">({day})</span>
              </span>
              <span className="title2medium text-db-text">{time}</span>
            </div>
          </div>

          {/* User info and logout */}
          <div className="flex items-center h-full min-w-0 md:min-w-[218px] bg-header-content justify-between px-[10px] md:px-[20px] gap-[8px] md:gap-[12px]">
            <div className="">
              <SwitchButton typeButton="text" id="btnTranslate" groupKey="btnTranslate" />
            </div>

            <Button
              variant="ghost"
              type="button"
              className="flex flex-row items-center gap-[4px] h-auto p-0 hover:bg-transparent cursor-pointer hover:opacity-80"
              onClick={() => setIsModalOpen(true)}
            >
              <img src={IconUser} alt="user" className="h-[24px] icon-adaptive" />
              <span className="hidden md:inline max-w-[140px] truncate title2medium text-db-text">
                {userInfo?.name}
              </span>
            </Button>

            {/* <NavLink to="/dashboard/notif-dashboard" className="icon-adaptive">
              <Bell size={22} />
            </NavLink> */}

            <div className="flex items-center gap-[4px]">
              <ThemeToggle className="text-db-text" />
              <Button
                variant="ghost"
                type="button"
                className="h-auto p-0 hover:bg-transparent"
                onClick={handleLogoutButton}
              >
                <img
                  src={IconLogout}
                  alt="Logout"
                  className="cursor-pointer hover:opacity-80 icon-adaptive"
                />
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile navigation drawer (below lg) */}
      {isNavOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <button
            type="button"
            aria-label="Close navigation"
            className="absolute inset-0 bg-black/50"
            onClick={() => setIsNavOpen(false)}
          />

          <aside className="absolute left-0 top-0 flex h-full w-[260px] max-w-[80vw] flex-col bg-db-black border-r border-db-border p-[16px]">
            <div className="flex items-center justify-between pb-[12px]">
              <Link to={logoRoute} onClick={() => setIsNavOpen(false)}>
                <img
                  src={isDark ? LogoLight : Logo}
                  alt="logo"
                  className="h-[36px] w-auto object-contain"
                />
              </Link>
              <Button
                variant="ghost"
                type="button"
                aria-label="Close navigation"
                className="h-auto p-0 text-db-text hover:bg-transparent hover:opacity-80"
                onClick={() => setIsNavOpen(false)}
              >
                <X size={20} />
              </Button>
            </div>

            {/* Date/time is hidden in the bar below md, so surface it here */}
            <div className="flex flex-col gap-[4px] border-y border-db-border py-[12px] md:hidden">
              <span className="title2 text-db-text">
                {date} ({day})
              </span>
              <span className="title2medium text-db-text">{time}</span>
            </div>

            <nav className="flex flex-col gap-1 pt-[12px] overflow-y-auto">
              {visibleHeaderItems.map((item) => (
                <NavLink
                  key={item.name}
                  to={item.link}
                  onClick={() => setIsNavOpen(false)}
                  className={cn(
                    'title1 px-4 py-3 rounded-[12px] capitalize transition-colors',
                    isPathActive(item.activePaths)
                      ? 'bg-[#007AFF]/10 text-text-blue-100'
                      : 'text-text-grey'
                  )}
                >
                  {navLabel(item)}
                </NavLink>
              ))}
            </nav>
          </aside>
        </div>
      )}
      {userInfo?.account_id && (
        <ModalProfileManagement
          isModalOpen={isModalOpen}
          setIsModalOpen={setIsModalOpen}
          userInfo={userInfo as unknown as { account_id: string; [key: string]: any }}
          commonData={commonListData}
        />
      )}
    </>
  );
};

export default Header;
